import HashtagRail from "./components/HashtagRail";
import Bottom from "../../components/Layouts/Bottom";
import HeaderSearch from "./components/HeaderSearch";
import StoryRail from "./components/StoryRail";
import UserRail from "./components/UserRail";
import VideoRail from "./components/VideoRail";
import EmptyScreen from "../../components/common/EmptyScreen";
import Loader from "../../components/common/Loader";
import LoaderDark from "../../components/common/LoaderDark/LoaderDark";
import { useLoginModalContext } from "../../app/context/LoginModal";
import gluedin from "gluedin-shorts-js";
import React, { useEffect, useRef, useState } from "react";

const CuratedContentModule = new gluedin.GluedInCuratedContentModule();
const DISCOVER_RAILS_PAGE_SIZE = 5;
const DISCOVER_SCROLL_THRESHOLD = 250;

const Rail = ({ rail }: any) => {
  const rails: any = {
    video: <VideoRail key={rail.id} data={rail} />,
    user: <UserRail data={rail} />,
    hashtag: <HashtagRail key={rail.id} data={rail} />,
    story: <StoryRail data={rail} />,
    series: <VideoRail key={rail.id} data={rail} />,
  };
  return rails[rail.contentType];
};

const Discover = (props: any) => {
  const [data, setData]: any = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(0);
  const isFetchingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const paginationRafRef = useRef<number | null>(null);
  const contentListRef = useRef<HTMLDivElement | null>(null);
  const { setShowLoginModal } = useLoginModalContext();

  const getRailKey = (rail: any) =>
    String(
      rail?.railId ??
        rail?.id ??
        rail?._id ??
        `${rail?.contentType ?? "rail"}-${rail?.railName ?? ""}`,
    );

  const filterRails = (rails: any[]) =>
    rails.filter(
      (item: any) => Array.isArray(item?.itemList) && item?.itemList.length > 0,
    );

  const mergeUniqueRails = (current: any[], incoming: any[]) => {
    const merged = [...current];
    const existingKeys = new Set(current.map((rail: any) => getRailKey(rail)));
    incoming.forEach((rail: any) => {
      const key = getRailKey(rail);
      if (!existingKeys.has(key)) {
        existingKeys.add(key);
        merged.push(rail);
      }
    });
    return merged;
  };

  const updateHasMore = (value: boolean) => {
    hasMoreRef.current = value;
    setHasMore(value);
  };

  const fetchDiscoverPage = async (targetPage: number) => {
    if (isFetchingRef.current) return;
    if (targetPage > 1 && !hasMoreRef.current) return;

    isFetchingRef.current = true;
    if (targetPage === 1) {
      setIsInitialLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const curatedContentModuleResponse =
        await CuratedContentModule.getCuratedContentList({
          placement: "discover",
          offset: targetPage,
          limit: DISCOVER_RAILS_PAGE_SIZE,
        });

      if (curatedContentModuleResponse?.status === 200) {
        const apiResult = Array.isArray(curatedContentModuleResponse?.data?.result)
          ? curatedContentModuleResponse.data.result
          : [];
        const filteredData = filterRails(apiResult);

        setData((prevData: any[]) =>
          targetPage === 1 ? filteredData : mergeUniqueRails(prevData, filteredData),
        );

        pageRef.current = targetPage;

        const totalFromApi = Number(curatedContentModuleResponse?.data?.total);
        if (Number.isFinite(totalFromApi) && totalFromApi > 0) {
          updateHasMore(targetPage * DISCOVER_RAILS_PAGE_SIZE < totalFromApi);
        } else {
          updateHasMore(apiResult.length >= DISCOVER_RAILS_PAGE_SIZE);
        }
      } else {
        if (targetPage === 1) {
          setData([]);
        }
        updateHasMore(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      isFetchingRef.current = false;
      setIsInitialLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    void fetchDiscoverPage(1);
  }, []);

  useEffect(() => {
    const container = contentListRef.current;
    if (!container) return;

    const onScroll = () => {
      if (paginationRafRef.current !== null) return;
      paginationRafRef.current = requestAnimationFrame(() => {
        paginationRafRef.current = null;
        if (isFetchingRef.current || !hasMoreRef.current) return;

        const scrollTop = container.scrollTop;
        const clientHeight = container.clientHeight;
        const scrollHeight = container.scrollHeight;

        const hasReachedBottom =
          scrollTop + clientHeight >= scrollHeight - DISCOVER_SCROLL_THRESHOLD;

        if (hasReachedBottom) {
          void fetchDiscoverPage(pageRef.current + 1);
        }
      });
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (paginationRafRef.current !== null) {
        cancelAnimationFrame(paginationRafRef.current);
        paginationRafRef.current = null;
      }
    };
  }, [isInitialLoading, data.length]);

  if (isInitialLoading) return <Loader />;

  const loginModalHandler = () => {
    setShowLoginModal(true);
  };

  return (
    <>
      <div className="full-sec"><div
          className="right-sec profile-content-list discover-top"
          ref={contentListRef}
          style={{ height: "100dvh", overflowY: "auto" }}
        >
          <HeaderSearch redirect={true} />
          {data.length === 0 ? (
            <EmptyScreen
              title="No content available for this view."
              fullHeight={true}
            />
          ) : (
            data.map((rail: any, index: number) => (
              <Rail
                key={`${rail?.id ?? rail?.contentType ?? "rail"}-${index}`}
                rail={rail}
              />
            ))
          )}
          {hasMore && isLoadingMore && (
            <div className="d-flex justify-content-center py-3">
              <LoaderDark />
            </div>
          )}
        </div>
      </div>
      <Bottom />
    </>
  );
};

export default Discover;
